import React, { createContext, useContext, useState, PropsWithChildren, useEffect } from 'react';

export type PopoverState = {
	openPopoverId: string | null;
	openPopover: (popoverId: string, sharedId?: string) => void;
	closePopover: () => void;
	isPopoverOpen: (popoverId: string) => boolean;
	isPopoverOwner: (popoverId: string) => boolean;
	isSharedGroupOpen: (sharedId: string) => boolean; // Check if any popover in shared group is open
};

// Legacy aliases for backwards compatibility
export type ContextMenuState = PopoverState;

// Global state to ensure only one popover can be open across all React trees (including dialogs)
let globalOpenPopoverId: string | null = null;
let globalSharedId: string | null = null;
const popoverListeners: Set<(popoverId: string | null, sharedId: string | null) => void> = new Set();

const notifyListeners = (popoverId: string | null, sharedId: string | null = null) => {
	globalOpenPopoverId = popoverId;
	globalSharedId = sharedId;
	popoverListeners.forEach((listener) => listener(popoverId, sharedId));
};

// Global click listener to close popovers when clicking outside
const handleGlobalClick = (event: MouseEvent) => {
	// Prevent closing popovers when clicking on:
	// - [role="menu"]: MUI Menu components
	// - [data-context-menu-trigger]: Context menu trigger buttons
	// - .MuiPopover-root: Popover containers (e.g., ExpressionPlaceholder, VarPathSelectTree)
	// - .MuiDialog-root: Dialog overlays (e.g., VarPathNewVariableDialog) - prevents dialog clicks from closing underlying popovers
	const target = event.target as Element;
	if (
		target &&
		!target.closest('[role="menu"]') &&
		!target.closest('[data-context-menu-trigger]') &&
		!target.closest('.MuiPopover-root') &&
		!target.closest('.MuiDialog-root')
	) {
		notifyListeners(null, null);
	}
};

// Set up global click listener
if (typeof window !== 'undefined') {
	document.addEventListener('click', handleGlobalClick, true);
}

const ContextMenuContext = createContext<PopoverState>(undefined!);

export const ContextMenuProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [openPopoverId, setOpenPopoverId] = useState<string | null>(globalOpenPopoverId);
	const [sharedId, setSharedId] = useState<string | null>(globalSharedId);

	useEffect(() => {
		const listener = (popoverId: string | null, sharedId: string | null) => {
			setOpenPopoverId(popoverId);
			setSharedId(sharedId);
		};

		popoverListeners.add(listener);

		return () => {
			popoverListeners.delete(listener);
		};
	}, []);

	const openPopover = (popoverId: string, sharedId?: string) => {
		notifyListeners(popoverId, sharedId || null);
	};

	const closePopover = () => {
		notifyListeners(null, null);
	};

	const isPopoverOpen = (popoverId: string) => {
		return openPopoverId === popoverId;
	};

	const isPopoverOwner = (popoverId: string) => {
		return openPopoverId === popoverId;
	};

	const isSharedGroupOpen = (sharedGroupId: string) => {
		return sharedId === sharedGroupId;
	};

	const value: PopoverState = {
		openPopoverId,
		openPopover,
		closePopover,
		isPopoverOpen,
		isPopoverOwner,
		isSharedGroupOpen,
	};

	return <ContextMenuContext.Provider value={value}>{children}</ContextMenuContext.Provider>;
};

export const useContextMenu = () => {
	const context = useContext(ContextMenuContext);
	if (!context) {
		throw new Error('useContextMenu must be used within a ContextMenuProvider');
	}

	// Legacy compatibility - provide both new and old method names
	return {
		...context,
		// Legacy aliases for backwards compatibility
		openMenuId: context.openPopoverId,
		openContextMenu: context.openPopover,
		closeContextMenu: context.closePopover,
		isMenuOpen: context.isPopoverOpen,
		isMenuOwner: context.isPopoverOwner,
	};
};

// New hook with cleaner naming
export const usePopover = () => {
	const context = useContext(ContextMenuContext);
	if (!context) {
		throw new Error('usePopover must be used within a ContextMenuProvider');
	}
	return context;
};

export default ContextMenuContext;
